import { LoadingComponent, useLogger } from '@amzn/event-engine-js-utils';
import { AuthContext, IdentityProvider } from 'contexts/AuthProvider';
import React, { FC, useContext, useEffect, useState } from 'react';
import { i18nStrings } from 'utils/constants';

export interface AuthGuardProps {
    identityProvider?: IdentityProvider;
}

/**
 * Wrapper component that conditionally renders children
 * if a user is authenticated
 */
const AuthGuard: FC<AuthGuardProps> = ({ children, identityProvider }) => {
    const { signIn, getCurrentSession } = useContext(AuthContext);
    const [isAuthenticated, setIsAuthenticated] = useState<boolean>();
    const [logger] = useLogger('AuthGuard');

    useEffect(() => {
        if (isAuthenticated === undefined) {
            return;
        }

        if (!isAuthenticated) {
            logger.info(
                'User is not authenticated, redirecting to login provider'
            );
            signIn(identityProvider);
        }
    }, [isAuthenticated]);

    useEffect(() => {
        getCurrentSession(identityProvider).then((currentSession) =>
            setIsAuthenticated(!!currentSession)
        );
    }, []);

    if (isAuthenticated === null) {
        return <LoadingComponent containerProps={{ margin: 'n' }} />;
    } else if (!isAuthenticated) {
        return (
            <LoadingComponent
                containerProps={{ margin: 'n' }}
                spinnerWithLabelProps={{
                    size: 'big',
                    children: i18nStrings.signIn.signingIn,
                    textVariant: 'h5',
                }}
            />
        );
    }

    return <>{children}</>;
};

export default AuthGuard;
